import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import './InputControl.scss';
import Label from '../Label/Label';
import { createClassList } from '../../utils/dom/class';

const InputControl = ({
  children,
  error,
  hideLabel,
  id,
  classes,
  label,
  labelModifier,
  microcopy,
  ariaLabel,
}) => {
  let errorContent = '';
  let microcopyContent = '';
  let requiredClasses = [];

  if (error) {
    errorContent = (
      <span
        id={`${id}_error`}
        className='input-control--error--message'
      >
        {error}
      </span>
    );
    requiredClasses = [...requiredClasses, 'input-control--error'];
  }
  if (!error && microcopy) {
    const microcopyClasses = ['input-control--microcopy'];
    microcopyContent = (
      <span
        id={`${id}_microcopy`}
        className={microcopyClasses.join(' ')}
      >
        {microcopy}
      </span>
    );
  }

  // Store a reference to the element whose "id" attribute corresponds to the "htmlFor" prop on the <Label> component. We'll only use this in the uncommon case where we need to replicate the browser default behavior on non-native input elements.
  const [labelTarget, setLabelTarget] = useState(null);

  useEffect(() => {
    const domField = document.getElementById(id);
    setLabelTarget(domField);
  }, []);

  const handleLabelClick = () => {
    // Replicate browser default behavior where clicking on a <label> with a 'for' attribute corresponding to an input 'id' focuses that input.
    if (labelTarget !== null) {
      // Only do something if the associated input is not an actual input element but needs to behave like one (e.g. SelectInput).
      if (labelTarget.nodeName !== 'INPUT') {
        labelTarget.focus();
      }
    }
  };

  return (
    <div
      data-testid='input-control'
      className={createClassList({
        default: ['input-control'],
        custom: classes || [],
        required: requiredClasses,
      })}
    >
      <Label
        error={error}
        hideLabel={hideLabel}
        label={label}
        labelForId={id}
        labelModifier={labelModifier}
        ariaLabel={ariaLabel}
        labelClickHandler={handleLabelClick}
      />
      {children}
      {errorContent || microcopyContent ? (
        <span id={`${id}_messages`}>
          {[errorContent, microcopyContent]}
        </span>
      ) : null}
    </div>
  );
};

InputControl.propTypes = {
  label: PropTypes.string.isRequired,
  hideLabel: PropTypes.bool,
  id: PropTypes.string.isRequired,
  labelModifier: PropTypes.oneOf(['required', 'optional']),
  error: PropTypes.string,
  microcopy: PropTypes.string,
  children: PropTypes.node.isRequired,
  ariaLabel: PropTypes.string,
  classes: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
};

InputControl.defaultProps = {
  error: undefined,
  hideLabel: undefined,
  labelModifier: null,
  microcopy: undefined,
  ariaLabel: undefined,
  classes: [],
};

export default InputControl;
